import type { AgentActivityKind, CreationStage } from './agent-activity-types';

import type { RootState } from 'js/src/libs/RootState';

/**
 * What the agent is doing to this scene — the single answer, so surfaces don't
 * re-derive the precedence. `building` beats `reviewing` (nothing to review over yet),
 * and both beat `editing`, which every non-idle scene also matches.
 *
 * Returns a string, so it's safe to pass straight to `useSelector`.
 */
export function getAgentActivityKind(state: RootState, sceneId: string): AgentActivityKind {
    const activity = state.agentActivity;
    if (!activity) {
        return 'idle';
    }

    const progress = activity.progress.get(sceneId);
    if (progress !== undefined && !progress.complete) {
        return 'building';
    }
    if (activity.reviewing.has(sceneId)) {
        return 'reviewing';
    }
    if (activity.turn.has(sceneId) || activity.pending.has(sceneId)) {
        return 'editing';
    }
    return 'idle';
}

/**
 * Whether this scene should be blurred regardless of its activity kind. A `building`
 * scene always blurs; this additionally covers voicedriven base scenes, which read as
 * `editing` because no fill ever arrives for them but whose content isn't ready yet.
 */
export function isSceneBlurred(state: RootState, sceneId: string): boolean {
    const activity = state.agentActivity;
    if (!activity) {
        return false;
    }
    return activity.blurred.has(sceneId) || activity.pending.has(sceneId);
}

export function isReviewSkipped(state: RootState): boolean {
    return state.agentActivity?.reviewSkipped ?? false;
}

export function isReviewInProgress(state: RootState): boolean {
    return state.agentActivity?.reviewStarted ?? false;
}

/** Which stage of a from-scratch build is underway, or null if none is. */
export function getCreationStage(state: RootState): CreationStage | null {
    return state.agentActivity?.creationStage ?? null;
}
